import axios from 'axios';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Define the types for the Kafka lag response
export interface KafkaPartition {
  partition: number;
  offset: string;
  metadata: string | null;
}

export interface KafkaLagMetric {
  cluster: string;
  groupId: string;
  topic: string;
  clientId: string;
  partition: KafkaPartition;
  lag: number;
  totalPartitions: number;
  timestamp: string;
}

export interface KafkaLagResponse {
  metrics: KafkaLagMetric[];
  count: number;
  start_time: string;
  end_time: string;
  cluster: string;
}

// Define the state interface
interface KafkaLagState {
  metrics: KafkaLagMetric[];
  count: number;
  cluster: string;
  loading: boolean;
  error: string | null;
}

const initialState: KafkaLagState = {
  metrics: [],
  count: 0,
  cluster: '',
  loading: false,
  error: null,
};

// Set the API base URL - use the proxy to avoid CORS issues
const KAFKA_LAG_API_URL = 'https://api.status.gridbit.live/api/v1/kafka/lag';

// Create async thunk for fetching Kafka lag metrics
export const fetchKafkaLag = createAsyncThunk(
  'kafkaLag/fetchKafkaLag',
  async (cluster: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${KAFKA_LAG_API_URL}?cluster=${cluster}&force_fresh=true`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching Kafka lag metrics:', error);
      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch Kafka lag metrics');
    }
  }
);

// Create the Kafka lag slice
export const kafkaLagSlice = createSlice({
  name: 'kafkaLag',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchKafkaLag.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKafkaLag.fulfilled, (state, action: PayloadAction<KafkaLagResponse>) => {
        state.loading = false;
        state.metrics = action.payload.metrics;
        state.count = action.payload.count;
        state.cluster = action.payload.cluster;
      })
      .addCase(fetchKafkaLag.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default kafkaLagSlice.reducer; 